import { useState } from 'react';
import './securee.css';
import Navbar from '../../Navbar/nbar';

/* ═══════════════════════════════════════
   SVG DATA CHARTS
   ═══════════════════════════════════════ */
function ChartGap({ mini }) {
  const data = [
    { label: 'No Budget', pct: 73, color: '#C0392B' },
    { label: 'No Emergency Fund', pct: 68, color: '#C0392B' },
    { label: 'No Insurance', pct: 59, color: '#A67C00' },
    { label: 'No Investments', pct: 54, color: '#A67C00' },
    { label: 'High-Interest Debt', pct: 41, color: '#4f297e' },
  ];
  if (mini) return (
    <svg viewBox="0 0 120 80" className="sec__chart-mini">
      {data.map((d, i) => (
        <g key={i}>
          <rect x="0" y={i * 15 + 2} width={d.pct * 1.1} height="10" rx="3" fill={d.color} opacity="0.6" />
          <text x={d.pct * 1.1 + 4} y={i * 15 + 10} fill="#8E90A6" fontSize="6" fontWeight="600">{d.pct}%</text>
        </g>
      ))}
    </svg>
  );
  return (
    <svg viewBox="0 0 440 220" className="sec__chart">
      <text x="10" y="18" className="sec__ct">Financial Readiness Gap — Indian Households</text>
      {data.map((d, i) => {
        const y = 38 + i * 36;
        return (
          <g key={i}>
            <text x="10" y={y + 15} className="sec__cl">{d.label}</text>
            <rect x="140" y={y + 2} width="260" height="20" rx="5" fill="#F0F0F5" />
            <rect x="140" y={y + 2} width={260 * d.pct / 100} height="20" rx="5" fill={d.color} opacity="0.65">
              <animate attributeName="width" from="0" to={260 * d.pct / 100} dur="1s" fill="freeze" />
            </rect>
            <text x={145 + 260 * d.pct / 100} y={y + 16} className="sec__cp">{d.pct}%</text>
          </g>
        );
      })}
      <text x="140" y="215" className="sec__cs">Source: RBI Financial Inclusion Report, SEBI Surveys, Industry Research</text>
    </svg>
  );
}

function ChartCompound({ mini }) {
  const pts = [];
  for (let y = 0; y <= 25; y++) {
    const r = 0.12 / 12, n = y * 12;
    const fv = n > 0 ? 10000 * ((Math.pow(1 + r, n) - 1) / r) * (1 + r) : 0;
    pts.push({ y, fv, inv: 10000 * n });
  }
  const max = pts[25].fv;
  const sx = y => 50 + (y / 25) * 340;
  const sy = v => 30 + 150 - (v / max) * 150;

  if (mini) {
    const msx = y => 5 + (y / 25) * 110;
    const msy = v => 5 + 65 - (v / max) * 65;
    const line = pts.filter((_, i) => i % 3 === 0 || i === 25).map((p, i) => `${i === 0 ? 'M' : 'L'} ${msx(p.y)} ${msy(p.fv)}`).join(' ');
    const area = line + ` L ${msx(25)} ${msy(0)} L ${msx(0)} ${msy(0)} Z`;
    return (
      <svg viewBox="0 0 120 80" className="sec__chart-mini">
        <path d={area} fill="#1A8A63" opacity="0.1" />
        <path d={line} fill="none" stroke="#1A8A63" strokeWidth="2" />
        <circle cx={msx(25)} cy={msy(pts[25].fv)} r="3" fill="#1A8A63" />
      </svg>
    );
  }

  const lineFV = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.y)} ${sy(p.fv)}`).join(' ');
  const lineInv = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.y)} ${sy(p.inv)}`).join(' ');
  const areaFV = lineFV + ` L ${sx(25)} ${sy(0)} L ${sx(0)} ${sy(0)} Z`;

  return (
    <svg viewBox="0 0 440 220" className="sec__chart">
      <text x="50" y="18" className="sec__ct">₹10,000/month SIP at 12% — 25 Year Projection</text>
      <defs>
        <linearGradient id="gfv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1A8A63" stopOpacity="0.15"/><stop offset="100%" stopColor="#1A8A63" stopOpacity="0"/></linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
        <line key={i} x1="50" y1={sy(max * f)} x2="390" y2={sy(max * f)} stroke="#F0F0F5" strokeWidth="0.8" />
      ))}
      <path d={areaFV} fill="url(#gfv)" />
      <path d={lineFV} fill="none" stroke="#1A8A63" strokeWidth="2.5" strokeLinecap="round" />
      <path d={lineInv} fill="none" stroke="#4f297e" strokeWidth="1.5" strokeDasharray="5 4" />
      {[0, 5, 10, 15, 20, 25].map(y => <text key={y} x={sx(y)} y="205" textAnchor="middle" className="sec__ca">{y}y</text>)}
      <circle cx={sx(25)} cy={sy(pts[25].fv)} r="5" fill="#1A8A63" />
      <circle cx={sx(25)} cy={sy(pts[25].inv)} r="4" fill="#4f297e" />
      <rect x="60" y="28" width="10" height="10" rx="2" fill="#1A8A63" opacity="0.5" /><text x="74" y="37" className="sec__cleg">Total Value (₹1.9 Cr)</text>
      <rect x="190" y="28" width="10" height="10" rx="2" fill="#4f297e" opacity="0.5" /><text x="204" y="37" className="sec__cleg">Invested (₹30 L)</text>
    </svg>
  );
}

function ChartTax({ mini }) {
  const secs = [
    { l: '80C', a: 1.5, c: '#4f297e' }, { l: '80CCD', a: 0.5, c: '#6b3fa3' },
    { l: '80D', a: 0.75, c: '#1A8A63' }, { l: '24(b)', a: 2.0, c: '#A67C00' }, { l: 'HRA', a: 1.2, c: '#555770' },
  ];
  const total = secs.reduce((a, s) => a + s.a, 0);
  if (mini) {
    let acc = 0;
    return (
      <svg viewBox="0 0 120 80" className="sec__chart-mini">
        {secs.map((s, i) => { const w = (s.a / total) * 110; const x = 5 + acc; acc += w + 2; return <rect key={i} x={x} y="15" width={w} height="50" rx="4" fill={s.c} opacity="0.55" />; })}
      </svg>
    );
  }
  let acc = 0;
  return (
    <svg viewBox="0 0 440 180" className="sec__chart">
      <text x="10" y="18" className="sec__ct">Tax Deduction Architecture — Maximum Annual Benefit</text>
      {secs.map((s, i) => { const w = (s.a / total) * 360; const x = 40 + acc; acc += w + 6; return (
        <g key={i}>
          <rect x={x} y="40" width={w} height="60" rx="8" fill={s.c} opacity="0.6"><animate attributeName="height" from="0" to="60" dur="0.8s" fill="freeze" /><animate attributeName="y" from="100" to="40" dur="0.8s" fill="freeze" /></rect>
          <text x={x + w / 2} y="76" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700">{s.l}</text>
          <text x={x + w / 2} y="120" textAnchor="middle" className="sec__cv">₹{s.a}L</text>
        </g>
      ); })}
      <text x="220" y="150" textAnchor="middle" className="sec__ctotal">Total: ₹{total.toFixed(1)}L/year → ₹{(total * 0.3).toFixed(1)}L saved at 30%</text>
    </svg>
  );
}

function ChartEmergency({ mini }) {
  const mo = [0, 6, 12, 18, 24, 30, 36];
  const vals = [0, 31500, 64600, 99400, 136000, 174500, 215000];
  const max = 215000;
  const sx = m => 50 + (m / 36) * 340;
  const sy = v => 30 + 140 - (v / max) * 140;
  const line = mo.map((m, i) => `${i === 0 ? 'M' : 'L'} ${sx(m)} ${sy(vals[i])}`).join(' ');
  const area = line + ` L ${sx(36)} ${sy(0)} L ${sx(0)} ${sy(0)} Z`;

  if (mini) {
    const msx = m => 5 + (m / 36) * 110;
    const msy = v => 5 + 65 - (v / max) * 65;
    const ml = mo.map((m, i) => `${i === 0 ? 'M' : 'L'} ${msx(m)} ${msy(vals[i])}`).join(' ');
    return (
      <svg viewBox="0 0 120 80" className="sec__chart-mini">
        <path d={ml + ` L ${msx(36)} ${msy(0)} L ${msx(0)} ${msy(0)} Z`} fill="#1A8A63" opacity="0.08" />
        <path d={ml} fill="none" stroke="#1A8A63" strokeWidth="2" />
        <line x1={msx(0)} y1={msy(180000)} x2={msx(36)} y2={msy(180000)} stroke="#A67C00" strokeWidth="1" strokeDasharray="3 2" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 440 210" className="sec__chart">
      <text x="50" y="18" className="sec__ct">Emergency Fund Growth — ₹5,000/month in Liquid Fund (7%)</text>
      <defs><linearGradient id="gem" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1A8A63" stopOpacity="0.12"/><stop offset="100%" stopColor="#1A8A63" stopOpacity="0"/></linearGradient></defs>
      <path d={area} fill="url(#gem)" />
      <path d={line} fill="none" stroke="#1A8A63" strokeWidth="2.5" strokeLinecap="round" />
      {mo.map((m, i) => (
        <g key={i}>
          <circle cx={sx(m)} cy={sy(vals[i])} r="4" fill="#fff" stroke="#1A8A63" strokeWidth="2" />
          <text x={sx(m)} y="195" textAnchor="middle" className="sec__ca">{m}m</text>
          {i > 0 && <text x={sx(m)} y={sy(vals[i]) - 10} textAnchor="middle" className="sec__cdot">₹{(vals[i]/1000).toFixed(0)}K</text>}
        </g>
      ))}
      <line x1="50" y1={sy(180000)} x2="390" y2={sy(180000)} stroke="#A67C00" strokeWidth="1.2" strokeDasharray="5 4" />
      <text x="392" y={sy(180000) + 4} className="sec__ctarget">6-mo target</text>
    </svg>
  );
}

/* ═══════════════════════════════════════
   DATA
   ═══════════════════════════════════════ */
const ARTICLES = [
  { id: 'gap', cat: 'Research Insight', title: 'The Financial Readiness Gap', subtitle: 'in Indian Households',
    lead: 'Despite rapid economic growth, a significant gap persists between household income and financial preparedness. Understanding this gap is the first step toward closing it.',
    hero_stat: '73%', hero_label: 'without a formal budget', chart: 'gap',
    metrics: [{ v: '73%', l: 'Without budget' }, { v: '68%', l: 'No emergency fund' }, { v: '3.2%', l: 'Insurance/GDP' }],
    callout: '68% of Indian households lack an emergency fund covering 3 months of expenses.',
    sections: [
      { h: 'The Scale of the Problem', t: 'According to multiple industry surveys and RBI financial inclusion data, approximately 73% of Indian households operate without a formal budget. This is not primarily a function of income — studies show the pattern holds across income brackets, from ₹15,000/month households to ₹1.5 lakh/month earners. The absence of budgeting creates a cascade effect: without knowing where money goes, it becomes nearly impossible to save, invest, or plan systematically.' },
      { h: 'Emergency Fund Deficit', t: 'Around 68% of households lack an emergency fund capable of covering three months of essential expenses. This means a single job loss, medical event, or economic disruption forces families into high-interest debt — typically personal loans at 14–18% or credit card revolving credit at 36–42%. The irony: building a 3-month emergency fund requires only ₹5,000/month over 36 months for a family spending ₹30,000/month on essentials.' },
      { h: 'The Insurance Blind Spot', t: 'Life insurance penetration in India stands at approximately 3.2% of GDP — well below the global average of 3.9%. More critically, a substantial portion of existing policies are endowment and money-back plans that combine inadequate cover with poor investment returns (4–5% historically). Pure term insurance, which provides 10–20x more cover per rupee, remains underutilised despite costing a fraction of traditional plans.' },
      { h: 'Why Financial Literacy Is the Lever', t: 'The pattern across all these metrics — budgeting, emergency funds, insurance, investments — points to a single root cause: financial literacy. Not intelligence, not income, but specifically the knowledge of how financial instruments work and the behavioural frameworks to apply them. This is precisely the gap SOMFIN\'s platform is designed to close — through interactive tools, personalised roadmaps, and educational content calibrated to each user\'s starting point.' },
    ] },
  { id: 'compound', cat: 'Investment Fundamentals', title: 'The Mathematics of', subtitle: 'Patient Capital',
    lead: 'Compound interest is called the eighth wonder of the world for a reason. The gap between ₹30 lakh invested and ₹1.9 crore accumulated is not magic — it is mathematics applied over time.',
    hero_stat: '84%', hero_label: 'returns, not contributions', chart: 'compound',
    metrics: [{ v: '84%', l: 'Returns not contributions' }, { v: '50%', l: 'Lost per 5yr delay' }, { v: '2.2x', l: 'Step-up multiplier' }],
    callout: 'A 5-year delay in starting reduces a 25-year SIP corpus by 50% — from ₹1.9 Cr to ₹95 L.',
    sections: [
      { h: 'The Compounding Asymmetry', t: 'In a 25-year SIP at 12%, approximately 84% of the final corpus comes from returns — not contributions. This creates an asymmetry that rewards patience disproportionately. The first ₹10 lakh of returns takes roughly 7 years to accumulate. The last ₹10 lakh takes less than 8 months. This is not a linear process — it is exponential, and the exponent only becomes meaningful beyond year 10.' },
      { h: 'The Cost of Delay — Quantified', t: 'A 5-year delay in starting a ₹10,000/month SIP at 12% reduces the 25-year corpus from approximately ₹1.9 crore to ₹95 lakh — a 50% reduction caused by just 5 years of delay. Those first 5 years contribute only ₹7.5 lakh in actual money invested, but their compounding value over the remaining 20 years is worth approximately ₹95 lakh. Every year of delay has a multiplied cost that grows larger with time.' },
      { h: 'Rupee-Cost Averaging in Practice', t: 'When markets fall 20%, a monthly SIP automatically buys 25% more units at the same cost. When markets rise 20%, it buys fewer units. Over time, this mechanical process ensures the average purchase price is systematically below the average market price. This is not a strategy that requires market knowledge — it is an arithmetic property of fixed-amount periodic investing that works across all market conditions.' },
      { h: 'Step-Up SIP: The Amplifier', t: 'Increasing SIP contributions by 10% annually — roughly matching salary increments — transforms the outcome from ₹1.9 crore (flat SIP) to approximately ₹4.2 crore over 25 years. The additional effort is negligible (₹1,000 more per month in year 2, ₹2,100 more in year 3), but the compounding effect on the total corpus is a 2.2x multiplier. This single behavioural change — stepping up SIP with income growth — is the highest-impact wealth-building habit available.' },
    ] },
  { id: 'tax', cat: 'Tax Intelligence', title: 'Designing Your', subtitle: 'Tax-Efficient Architecture',
    lead: 'The Indian tax code offers over ₹5 lakh in deductions across multiple sections. Most taxpayers utilise less than half. The gap between compliance and optimisation is worth ₹1.5–2 lakh annually.',
    hero_stat: '₹5.9L', hero_label: 'max annual deductions', chart: 'tax',
    metrics: [{ v: '₹5.9L', l: 'Max deductions' }, { v: '₹1.8L', l: 'Tax saved at 30%' }, { v: '0.01%', l: 'NPS fund charge' }],
    callout: 'Section 80C represents only 25% of total deduction architecture available to salaried individuals.',
    sections: [
      { h: 'Beyond Section 80C', t: 'Most tax planning stops at the ₹1.5 lakh 80C limit — typically filled by EPF (automatically deducted), insurance premiums, and possibly PPF. But 80C represents only 25% of the total deduction architecture. Section 80CCD(1B) adds ₹50,000 for NPS contributions. Section 80D allows ₹25,000 for self/family health insurance plus ₹25,000–₹50,000 for parents. Section 24(b) provides ₹2 lakh for home loan interest. These sections compound to create a significantly larger deduction base.' },
      { h: 'ELSS vs PPF vs NPS', t: 'ELSS offers equity exposure with a 3-year lock-in — the shortest among 80C instruments — and has historically returned 12–15% over 10+ year periods. PPF guarantees approximately 7.1% with sovereign safety and EEE tax status (exempt at contribution, growth, and withdrawal). NPS offers the lowest fund management charges (0.01%) in Indian financial history plus an additional ₹50,000 deduction under 80CCD(1B). The optimal strategy for most salaried individuals involves using all three in proportion to their risk tolerance and time horizon.' },
      { h: 'Salary Structure as a Tax Tool', t: 'HRA exemption alone can save ₹50,000–₹1.5 lakh annually depending on rent paid and city of residence. Other restructuring opportunities include: LTA for domestic travel, meal vouchers (up to ₹2,200/month), telephone/internet reimbursements, and professional development allowances. A single conversation with your HR department about salary restructuring can yield ₹30,000–₹80,000 in annual tax savings with zero additional investment required.' },
      { h: 'Old vs New Regime', t: 'The new tax regime offers lower slab rates but eliminates most deductions. The break-even point varies by income level, but as a general framework: if your total claimable deductions (80C + 80CCD + 80D + HRA + 24b) exceed approximately ₹3.75 lakh at ₹12 lakh income, or ₹4.25 lakh at ₹15 lakh income, the old regime saves more. Below these thresholds, the new regime is typically advantageous.' },
    ] },
  { id: 'emergency', cat: 'Financial Foundation', title: 'The Emergency Fund as', subtitle: 'Financial Architecture',
    lead: 'An emergency fund is not a savings goal — it is structural infrastructure. Without it, every financial plan is built on a foundation that cracks under the first unexpected load.',
    hero_stat: '36mo', hero_label: 'to build full safety net', chart: 'emergency',
    metrics: [{ v: '36 mo', l: 'To build full fund' }, { v: '6–7%', l: 'Liquid fund return' }, { v: '₹5K/mo', l: 'Starting amount' }],
    callout: 'Without an emergency fund, a ₹50,000 medical bill becomes a ₹75,000 debt at 36% credit card interest.',
    sections: [
      { h: 'The Structural Role', t: 'The emergency fund serves a specific architectural function: it decouples unexpected expenses from long-term investments. Without this buffer, a ₹50,000 medical bill forces liquidation of equity mutual funds (potentially at a loss), premature FD withdrawal (with penalty), or credit card debt at 36–42% interest. Each of these responses converts a temporary cash-flow problem into a permanent wealth reduction. The emergency fund absorbs the shock so the rest of the financial structure remains intact.' },
      { h: 'Sizing the Fund Correctly', t: 'The target is 6 months of essential expenses — not 6 months of income. Essential expenses include: rent/EMI, food, utilities, insurance premiums, school fees, and minimum debt payments. Discretionary spending (dining out, entertainment, shopping) is excluded because in an actual emergency, these are the first things cut. For a family spending ₹30,000/month on essentials, the target is ₹1.8 lakh — not ₹3 lakh (which would be 6 months of total income).' },
      { h: 'Optimal Placement', t: 'The emergency fund has three requirements: safety, liquidity, and reasonable returns. Savings accounts satisfy the first two but yield only 3–4%. Liquid mutual funds historically deliver 6–7% with next-day redemption (many now offer instant access up to ₹50,000). A sweep-in FD automatically converts excess savings account balance into an FD while maintaining liquidity. The optimal strategy often combines all three: ₹50,000 in savings (immediate), ₹1 lakh in liquid fund (next-day), and the remainder in sweep FD.' },
      { h: 'The Build Trajectory', t: 'Starting from zero, a ₹5,000 monthly contribution to a liquid fund at 7% builds to approximately ₹2.15 lakh in 36 months. This exceeds the 6-month target for a household with ₹30,000/month essential expenses. The trajectory is not linear — month 1 contributes ₹5,000, but month 36 contributes ₹5,000 plus accumulated interest on all previous contributions. The key insight: the habit of consistent contribution matters more than the amount in the early months.' },
    ] },
];

const chartMap = { gap: <ChartGap />, compound: <ChartCompound />, tax: <ChartTax />, emergency: <ChartEmergency /> };
const miniMap = { gap: <ChartGap mini />, compound: <ChartCompound mini />, tax: <ChartTax mini />, emergency: <ChartEmergency mini /> };

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
export default function SecurePage() {
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState({});
  const toggle = (id, i) => { const k = `${id}-${i}`; setExpanded(p => ({ ...p, [k]: !p[k] })); };

  const article = active !== null ? ARTICLES[active] : null;

  return (
    <div className="sec__page">
      <Navbar />

      {/* ─── HERO with animated mesh ─── */}
      <section className="sec__hero">
        <div className="sec__hero-mesh" />
        <div className="sec__hero-glow" />
        <div className="sec__hero-inner">
          <span className="sec__tag">Research & Insights</span>
          <div className="sec__hero-title">The Knowledge Engine</div>
          <div className="sec__hero-subtitle">Behind Your Finances</div>
          <p className="sec__hero-sub">Deep research, real data, and actionable frameworks — every insight backed by RBI data, SEBI surveys, and quantitative analysis.</p>
          <div className="sec__hero-stats">
            {[{ v: '4', l: 'Research Papers' }, { v: '25+', l: 'Data Points' }, { v: '16', l: 'Deep Sections' }, { v: '4', l: 'Live Charts' }].map((s, i) => (
              <div key={i} className="sec__hero-stat" style={{ animationDelay: `${i * 150}ms` }}>
                <div className="sec__hero-stat-val">{s.v}</div>
                <div className="sec__hero-stat-lbl">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Floating data nodes */}
        <svg className="sec__hero-nodes" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid slice">
          <circle cx="120" cy="80" r="40" fill="none" stroke="rgba(79,41,126,0.08)" strokeWidth="1"><animate attributeName="r" values="38;44;38" dur="4s" repeatCount="indefinite" /></circle>
          <circle cx="680" cy="120" r="55" fill="none" stroke="rgba(26,138,99,0.06)" strokeWidth="1"><animate attributeName="r" values="52;60;52" dur="5s" repeatCount="indefinite" /></circle>
          <circle cx="400" cy="240" r="30" fill="none" stroke="rgba(79,41,126,0.05)" strokeWidth="1"><animate attributeName="r" values="28;34;28" dur="3.5s" repeatCount="indefinite" /></circle>
          <line x1="160" y1="80" x2="370" y2="240" stroke="rgba(79,41,126,0.04)" strokeWidth="0.8" />
          <line x1="430" y1="240" x2="625" y2="120" stroke="rgba(26,138,99,0.04)" strokeWidth="0.8" />
        </svg>
      </section>

      {/* ─── ARTICLE CARDS ─── */}
      <section className="sec__cards-section">
        <div className="sec__inner">
          <div className="sec__cards">
            {ARTICLES.map((a, i) => (
              <div key={i} className={`sec__card${active === i ? ' sec__card--on' : ''}`}
                onClick={() => { setActive(active === i ? null : i); setExpanded({}); }}
                role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && setActive(active === i ? null : i)}>
                <div className="sec__card-chart">{miniMap[a.chart]}</div>
                <div className="sec__card-body">
                  <div className="sec__card-cat">{a.cat}</div>
                  <div className="sec__card-title">{a.title} <em>{a.subtitle}</em></div>
                  <div className="sec__card-stat"><span className="sec__card-stat-val">{a.hero_stat}</span><span className="sec__card-stat-lbl">{a.hero_label}</span></div>
                </div>
                <div className="sec__card-cta">{active === i ? 'Reading ↑' : 'Read →'}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── READER MODAL ─── */}
      {article && (
        <div className="sec__reader-overlay" onClick={() => setActive(null)}>
          <div className="sec__reader" onClick={e => e.stopPropagation()}>
            <button className="sec__reader-close" onClick={() => setActive(null)} type="button">&times;</button>

            {/* Header band */}
            <div className="sec__reader-header">
              <div className="sec__reader-cat">{article.cat}</div>
              <div className="sec__reader-title">{article.title} <em>{article.subtitle}</em></div>
              <div className="sec__reader-metrics">
                {article.metrics.map((m, i) => (
                  <div key={i} className="sec__reader-metric">
                    <div className="sec__reader-metric-val">{m.v}</div>
                    <div className="sec__reader-metric-lbl">{m.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Lead + Callout */}
            <p className="sec__reader-lead">{article.lead}</p>
            <div className="sec__reader-callout">
              <div className="sec__reader-callout-line" />
              <p>{article.callout}</p>
            </div>

            {/* Full chart */}
            <div className="sec__chart-wrap">{chartMap[article.chart]}</div>

            {/* Expandable sections */}
            {article.sections.map((s, i) => {
              const k = `${article.id}-${i}`;
              const open = expanded[k] !== false;
              return (
                <div key={i} className={`sec__rsection${open ? ' sec__rsection--open' : ''}`}>
                  <button className="sec__rsection-head" onClick={() => toggle(article.id, i)} type="button">
                    <span className="sec__rsection-num">0{i + 1}</span>
                    <span className="sec__rsection-h">{s.h}</span>
                    <span className="sec__rsection-toggle">{open ? '−' : '+'}</span>
                  </button>
                  {open && <p className="sec__rsection-text">{s.t}</p>}
                </div>
              );
            })}

            <div className="sec__reader-disclaimer">
              This content is for general educational and informational purposes only. It does not constitute financial advice, investment recommendation, or professional guidance. Please consult a qualified financial advisor before making decisions.
            </div>

            {/* Prev/Next nav */}
            <div className="sec__reader-nav">
              <button className="sec__reader-nav-btn" disabled={active === 0} onClick={() => { setActive(active - 1); setExpanded({}); }} type="button">← Previous</button>
              <button className="sec__reader-nav-btn" disabled={active === ARTICLES.length - 1} onClick={() => { setActive(active + 1); setExpanded({}); }} type="button">Next →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}